import type { Answers, TSuppressionProfile } from "./_schema";
import { deriveIgniteProfile, type ExtraSignals } from "./ignite-profile";

/**
 * Cortisol-quiz deriver. Wraps `deriveIgniteProfile` (the quiz-v7 pattern)
 * with three additions:
 *   1. Answer translation: the quiz asks the ad's 8 signs in the ad's own
 *      language; the subset with a genuine ignite equivalent is translated
 *      into the ignite answer namespace (`tension` -> `stress-symptoms`).
 *      Passthrough ids (`age`, `energy-3pm`, `body-composition`,
 *      `when-started`) reuse ignite ids/values verbatim, so no translation.
 *   2. Sign severity: the 5 cortisol-only sign questions feed severity via
 *      the `extraSignals` seam (worst answer -> tier1, second-worst -> tier2,
 *      matching the quiz-v7 cohort-gradient convention).
 *   3. Forced primary: `cortisol-dominance` leads the verdict on every path
 *      via the `forcePrimaryCause` seam — the quiz's entire entry promise
 *      (ads, intro, both bridges) names cortisol, so the verdict must too.
 *      The organic top scorer stays visible as Secondary.
 *
 * Pure function of `answers` — reproducible server-side from the persisted
 * submission (rule 11).
 */

/** `tension` (sign 3, "tensed neck") is the deriver's stress question in ad language. */
const TENSION_TO_STRESS: Record<string, "yes" | "no"> = {
  constantly: "yes",
  "most-days": "yes",
  occasionally: "no",
  rarely: "no",
};

/** Ids whose values are ignite-verbatim — copied through untranslated. */
const PASSTHROUGH_IDS = ["age", "energy-3pm", "body-composition", "when-started"] as const;

/**
 * Sign-question severity mapping. Worst answer -> tier1, second-worst ->
 * tier2 (quiz-v7 cohort convention); sugar cravings sit one tier lower —
 * they are a weaker low-T correlate than libido/sleep-disruption/fog
 * (see docs/t-suppression-research.md tier rationale).
 */
const TIER1_SIGNS: Record<string, readonly string[]> = {
  "puffy-face": ["yes-noticeably"],
  "wake-pattern": ["most-nights"],
  "brain-fog": ["daily"],
  libido: ["significantly"],
};
const TIER2_SIGNS: Record<string, readonly string[]> = {
  "puffy-face": ["some-mornings"],
  "wake-pattern": ["few-times-week"],
  "brain-fog": ["most-days"],
  libido: ["somewhat"],
  "sugar-cravings": ["daily"],
};
const TIER3_SIGNS: Record<string, readonly string[]> = {
  "sugar-cravings": ["few-times-week"],
};

function countTier(answers: Answers, map: Record<string, readonly string[]>): number {
  let n = 0;
  for (const [id, values] of Object.entries(map)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) n += 1;
  }
  return n;
}

function signSignals(answers: Answers): ExtraSignals {
  return {
    tier1: countTier(answers, TIER1_SIGNS),
    tier2: countTier(answers, TIER2_SIGNS),
    tier3: countTier(answers, TIER3_SIGNS),
  };
}

/**
 * Builds the ignite-namespace answer set the base deriver reads. Only
 * genuinely-equivalent answers cross over — translation, not manufacture:
 * the user answered the stress question; the ad phrases it as tension.
 */
function syntheticAnswers(answers: Answers): Answers {
  const out: Answers = {};
  for (const id of PASSTHROUGH_IDS) {
    const v = answers[id];
    if (typeof v === "string") out[id] = v;
  }
  const tension = answers["tension"];
  if (typeof tension === "string" && tension in TENSION_TO_STRESS) {
    out["stress-symptoms"] = TENSION_TO_STRESS[tension]!;
  }
  return out;
}

export function deriveCortisolProfile(answers: Answers): TSuppressionProfile {
  return deriveIgniteProfile(syntheticAnswers(answers), {
    extraSignals: signSignals(answers),
    forcePrimaryCause: "cortisol-dominance",
  });
}
